import React, { useState } from "react";
import {
  Title,
  GoBack,
  MyMessage,
  InputField,
  MyActionButton,
} from "../../component/Component";
import { codeOk } from "../../../../middlewares/index";
import { FOLDERS } from "../../../doseeData/utils/Utils";
import { usePlugImageContext } from "../../../../context/plugImage/PlugImageContext";
import { getOnePlugImage } from "../../../../middlewares/sysNavigation/SysNavigation";
import { ImageKitUpload } from "../../../../middlewares/libraries/imageKit/ImageKitLibrary";

const Edit = () => {
  const { updatePlugImageState, updatePlugImage } = usePlugImageContext();
  const plugImage = getOnePlugImage();

  const [thumbnail, setThumbnail] = useState(plugImage?.photo_url);
  const [thumbnailId, setThumbnailId] = useState(plugImage?.image_cdn_id);
  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const plug_image_id = plugImage?.plug_image_id;
  const [type, setType] = useState(plugImage?.type);
  const plugImageRoutes = "/plug/image";

  const cleanFields = () => {
    setLoading(false);
    setSuccess(true);
  };

  const responses = (isError, msg) => {
    setError(isError);
    setLoading(false);
    setSuccess(!isError);
    setErrorMessage(msg);
  };

  const updateOnePlugImageHandler = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnail) {
      responses(true, "Plug Image Required");
    } else {
      await sendDBRequest();
    }
  };

  const sendDBRequest = async () => {
    const data = {
      type: type,
      photo_url: thumbnail,
      image_cdn_id: thumbnailId,
    };

    const { message, status } = await updatePlugImage(plug_image_id, data);

    if (status === codeOk) {
      updatePlugImageState({ plug_image_id, ...data });
      cleanFields();
      responses(false, message);
    } else {
      responses(true, message);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="mx-auto max-w-4xl p-4 lg:p-6">
        {/* Top bar */}
        <div className="mb-4 flex items-center justify-between">
          <GoBack props={{ route: plugImageRoutes }} />
          <span className="hidden rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 sm:inline-flex">
            Editing
          </span>
        </div>

        {/* Page header */}
        <div className="mb-3 flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-xl bg-gray-900 text-white">
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M19 3H5a2 2 0 00-2 2v14a1 1 0 001.555.832l4.89-3.26a2 2 0 012.11-.058l2.68 1.49a2 2 0 002.021-.083l4.58-3.053A2 2 0 0022 12.073V5a2 2 0 00-2-2z" />
            </svg>
          </span>
          <div>
            <h1 className="text-lg font-semibold tracking-tight text-gray-900">
              Edit Plug Image
            </h1>
            <p className="text-sm text-gray-600">
              Update the image and its type. Changes apply immediately.
            </p>
          </div>
        </div>

        {/* Alerts */}
        <div className="mb-4">
          <MyMessage
            error={error}
            message={message}
            success={success}
            onExpired={() => {
              setError(false);
              setSuccess(false);
            }}
            onClick={() => {
              setError(false);
              setSuccess(false);
              setErrorMessage("");
            }}
          />
        </div>

        {/* Form card */}
        <form onSubmit={updateOnePlugImageHandler}>
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            {/* Upload section */}
            <div className="p-4 lg:p-6">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <svg
                    className="h-4 w-4 text-gray-500"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M12 5l4 4h-3v6h-2V9H8l4-4z" />
                    <path d="M5 18h14v2H5z" />
                  </svg>
                  <span className="text-sm font-semibold text-gray-900">
                    Upload
                  </span>
                </div>
                <span className="text-xs text-gray-500">
                  JPG/PNG recommended
                </span>
              </div>

              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3">
                <ImageKitUpload
                  thumbnail={thumbnail}
                  thumbnailId={thumbnailId}
                  setThumbnail={setThumbnail}
                  setThumbnailId={setThumbnailId}
                  folder={FOLDERS.PLUGS}
                />
              </div>
              <p className="mt-2 text-[11px] text-gray-500">
                Use a clear, high-contrast image for best results.
              </p>
            </div>

            <div className="h-px bg-gray-100" />

            {/* Details */}
            <div className="p-4 lg:p-6">
              <div className="mb-3">
                <Title props={{ title: "Plug Image Details" }} />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                <InputField
                  props={{
                    id: "title",
                    title: "Type",
                    type: "text",
                    value: type ? type : "",
                    onChange: (e) => {
                      setType(e.target.value);
                      setError(null);
                      setSuccess(null);
                    },
                  }}
                />
              </div>
            </div>

            <div className="h-px bg-gray-100" />

            {/* Footer actions */}
            <div className="flex items-center justify-end gap-3 p-4 lg:p-6">
              <MyActionButton
                loading={loading}
                title="Save Changes"
                onClick={() => {
                  setError(false);
                  setSuccess(false);
                  setErrorMessage(null);
                }}
              />
            </div>
          </div>
        </form>

        <p className="mt-3 flex items-center gap-2 text-xs text-gray-500">
          <svg
            className="h-3.5 w-3.5"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M11 17h2v2h-2zM11 5h2v10h-2z" />
          </svg>
          Ensure the image matches the selected type for consistent display.
        </p>
      </div>
    </div>
  );
};

export default Edit;
